/**
 * Cache Cleaner Utility
 *
 * Clears browser Cache Storage and unregisters service workers to ensure
 * the latest bundle is loaded after a deployment. Does NOT clear localStorage
 * or sessionStorage.
 *
 * Compatible with Chrome, Edge, and Firefox (modern browsers with Cache API support).
 */

const CACHE_CLEANER_SESSION_KEY = "CACHE_CLEANER_RUN_THIS_SESSION";

/**
 * Deletes all cache entries from the Cache Storage API.
 * Safe to call when caches API is not available (e.g. non-secure context).
 */
async function clearCacheStorage(): Promise<void> {
  if (typeof caches === "undefined") {
    return;
  }

  try {
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map((name) => caches.delete(name)));
  } catch (err) {
    console.warn("[cacheCleaner] Failed to clear Cache Storage:", err);
  }
}

/**
 * Unregisters all registered service workers for the current scope.
 * Prevents stale service workers from serving old cached assets.
 */
async function unregisterServiceWorkers(): Promise<void> {
  if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    } catch (err) {
      console.warn("[cacheCleaner] Failed to unregister service workers:", err);
    }
  }
}

/**
 * Runs the full cache cleanup: clears Cache Storage and unregisters service workers.
 * Does not clear localStorage or sessionStorage.
 *
 * @returns Promise that resolves when cleanup is complete (rejects are caught and logged).
 */
export async function clearBrowserCache(): Promise<void> {
  await clearCacheStorage();
  await unregisterServiceWorkers();
}

/**
 * Returns whether the cache cleaner has already run this session.
 * Used to avoid running the cleaner again after a reload (prevents infinite reload loops).
 */
export function hasCacheCleanerRunThisSession(): boolean {
  try {
    return typeof sessionStorage !== "undefined" && !!sessionStorage.getItem(CACHE_CLEANER_SESSION_KEY);
  } catch {
    return false;
  }
}

/**
 * Marks that the cache cleaner has run this session.
 * Call this before triggering a reload so the next load does not run the cleaner again.
 */
export function setCacheCleanerRunThisSession(): void {
  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(CACHE_CLEANER_SESSION_KEY, "true");
    }
  } catch (err) {
    console.warn("[cacheCleaner] Failed to set session flag:", err);
  }
}

/**
 * Runs cache cleanup once per session and returns whether the caller should reload.
 * If this is the first load in the session: clears cache, sets session flag, returns true.
 * If already run this session: returns false (no reload needed).
 *
 * @returns Promise<boolean> - true if cache was cleared and page should reload, false otherwise.
 */
export async function clearCacheOnceAndRequestReload(): Promise<boolean> {
  if (hasCacheCleanerRunThisSession()) {
    return false;
  }

  await clearBrowserCache();
  setCacheCleanerRunThisSession();
  return true;
}
